import { Building2, User } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useListFirmRoleAssignmentsQuery } from '@/services/api/firmsService';
import {
  type OnePasswordConflictError,
  useOnepasswordCreateFromUrlMutation,
  useOnepasswordMainCredentialsQuery,
} from '@/services/api/onePasswordService';

interface AddOnePasswordDialogProps {
  isOpen: boolean;
  onClose: () => void;
  firmId: string;
  onAdded?: () => void;
}

const ONE_PASSWORD_TYPES = [
  { value: 'cms_credentials', label: 'CMS Credentials' },
] as const;

/**
 * Type guard to check if an error response is a OnePassword conflict error
 */
function isConflictError(error: unknown): error is { status: number; data: OnePasswordConflictError } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { status: number }).status === 409 &&
    'data' in error &&
    typeof (error as { data: unknown }).data === 'object' &&
    (error as { data: { code?: string } }).data !== null &&
    'code' in ((error as { data: object }).data)
  );
}

export const AddOnePasswordDialog: React.FC<AddOnePasswordDialogProps> = ({
  isOpen,
  onClose,
  firmId,
  onAdded,
}) => {
  const [privateUrl, setPrivateUrl] = useState('');
  const [type, setType] = useState<'cms_credentials'>('cms_credentials');
  const [credentialLevel, setCredentialLevel] = useState<'firm' | 'user'>('firm');
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  const [createFromUrl, { isLoading }] = useOnepasswordCreateFromUrlMutation();

  // Check if firm-level credentials already exist
  const { data: mainCredentials, isLoading: isLoadingMainCredentials } =
    useOnepasswordMainCredentialsQuery(
      { firmId, type: 'cms_credentials' },
      { skip: !isOpen || !firmId },
    );

  // Fetch users assigned to this firm
  const { data: firmRoleAssignments = [], isLoading: isLoadingUsers } =
    useListFirmRoleAssignmentsQuery(
      { firmPk: firmId },
      { skip: !isOpen || !firmId },
    );

  // Get unique users from role assignments
  const firmUsers = React.useMemo(() => {
    const userMap = new Map<string, { id: string; name: string; email: string }>();
    firmRoleAssignments.forEach((assignment) => {
      if (assignment.assignee && !userMap.has(assignment.assignee_id)) {
        userMap.set(assignment.assignee_id, {
          id: assignment.assignee_id,
          name: assignment.assignee.display_name || assignment.assignee.email,
          email: assignment.assignee.email,
        });
      }
    });
    return Array.from(userMap.values());
  }, [firmRoleAssignments]);

  // Firm has main credentials if the query succeeded (no 404)
  const hasMainCredentials = !!mainCredentials;

  // Can only add user-level credentials if main credentials exist
  const canAddUserLevel = hasMainCredentials;

  const handleAdd = async () => {
    if (!privateUrl.trim()) {
      toast.error('Please provide a OnePassword URL');
      return;
    }

    // Validate OnePassword URL format (supports both onepassword:// and https://start.1password.com/)
    if (
      !privateUrl.startsWith('onepassword://') &&
      !privateUrl.startsWith('https://start.1password.com/')
    ) {
      toast.error(
        'Please provide a valid OnePassword private URL (starts with https://start.1password.com/ or onepassword://)',
      );
      return;
    }

    // Validate user selection when user-level is chosen
    if (credentialLevel === 'user' && !selectedUserId) {
      toast.error('Please select a user for user-specific credentials');
      return;
    }

    // Determine user_id based on selection
    const effectiveUserId = credentialLevel === 'firm' ? undefined : selectedUserId || undefined;

    try {
      await createFromUrl({
        createOnePasswordItemFromUrlSchema: {
          private_url: privateUrl.trim(),
          type,
          firm_id: firmId,
          user_id: effectiveUserId,
        },
      }).unwrap();

      toast.success('OnePassword item added successfully');
      handleClose();
      onAdded?.();
    } catch (error) {
      console.error('Error adding OnePassword item:', error);

      // Handle specific conflict errors
      if (isConflictError(error)) {
        const conflictData = error.data;
        if (conflictData.code === 'duplicate_main_credentials') {
          toast.error(
            'This firm already has main credentials of this type. Each firm can only have one set of main credentials.',
          );
        } else if (conflictData.code === 'duplicate_user_credentials') {
          toast.error(
            'This user already has credentials for this firm. Each user can only have one set of credentials per firm.',
          );
        } else {
          toast.error(conflictData.error || 'Failed to add OnePassword item');
        }
      } else {
        toast.error('Failed to add OnePassword item');
      }
    }
  };

  const handleClose = () => {
    setPrivateUrl('');
    setType('cms_credentials');
    setCredentialLevel('firm');
    setSelectedUserId('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add OnePassword Item</DialogTitle>
          <DialogDescription>
            Add a OnePassword item by pasting the shareable link. You can get
            this by right-clicking an item in OnePassword and selecting
            &ldquo;Share&rdquo; → &ldquo;Get Shareable Link&rdquo;.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="type">Type</Label>
            <Select
              value={type}
              onValueChange={(value) => setType(value as 'cms_credentials')}
            >
              <SelectTrigger id="type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {ONE_PASSWORD_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="private-url">OnePassword Private URL</Label>
            <Input
              id="private-url"
              value={privateUrl}
              onChange={(e) => setPrivateUrl(e.target.value)}
              placeholder="https://start.1password.com/open/i?a=..."
              autoFocus
            />
            <p className="text-xs text-gray-500">
              Right-click the item in OnePassword and select &ldquo;Share&rdquo;
              → &ldquo;Get Shareable Link&rdquo;
            </p>
          </div>

          {/* Credential level selection */}
          {!isLoadingMainCredentials && (
            <div className="space-y-3">
              <Label>Credential Level</Label>
              <RadioGroup
                value={credentialLevel}
                onValueChange={(value) =>
                  setCredentialLevel(value as 'firm' | 'user')
                }
                className="space-y-2"
              >
                {/* Firm-level option - disabled if main credentials already exist */}
                <div
                  className={`flex items-center space-x-3 p-3 border rounded-lg ${
                    hasMainCredentials
                      ? 'opacity-50 cursor-not-allowed bg-gray-50'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <RadioGroupItem
                    value="firm"
                    id="level-firm"
                    disabled={hasMainCredentials}
                  />
                  <Label
                    htmlFor="level-firm"
                    className={`flex items-center gap-2 flex-1 ${
                      hasMainCredentials ? 'cursor-not-allowed' : 'cursor-pointer'
                    }`}
                  >
                    <Building2 className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="font-medium">Firm-level (Main)</p>
                      <p className="text-xs text-gray-500">
                        {hasMainCredentials
                          ? 'Main credentials already configured'
                          : 'Shared credentials for the entire firm'}
                      </p>
                    </div>
                  </Label>
                </div>

                {/* User-level option - disabled if no main credentials exist */}
                <div
                  className={`flex items-center space-x-3 p-3 border rounded-lg ${
                    !canAddUserLevel
                      ? 'opacity-50 cursor-not-allowed bg-gray-50'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <RadioGroupItem
                    value="user"
                    id="level-user"
                    disabled={!canAddUserLevel}
                  />
                  <Label
                    htmlFor="level-user"
                    className={`flex items-center gap-2 flex-1 ${
                      !canAddUserLevel ? 'cursor-not-allowed' : 'cursor-pointer'
                    }`}
                  >
                    <User className="h-4 w-4 text-purple-600" />
                    <div>
                      <p className="font-medium">User-specific</p>
                      <p className="text-xs text-gray-500">
                        {!hasMainCredentials
                          ? 'Requires firm-level credentials first'
                          : 'Personal credentials for a specific user'}
                      </p>
                    </div>
                  </Label>
                </div>
              </RadioGroup>

              {/* User selector - shown when user-level is selected */}
              {credentialLevel === 'user' && canAddUserLevel && (
                <div className="ml-6 mt-2 space-y-2">
                  <Label htmlFor="user-select">Select User</Label>
                  {firmUsers.length === 0 && !isLoadingUsers ? (
                    <p className="text-sm text-gray-500 italic">
                      No users assigned to this firm
                    </p>
                  ) : (
                    <Select
                      value={selectedUserId}
                      onValueChange={setSelectedUserId}
                      disabled={isLoadingUsers}
                    >
                      <SelectTrigger id="user-select">
                        <SelectValue placeholder={isLoadingUsers ? 'Loading users...' : 'Select a user'} />
                      </SelectTrigger>
                      <SelectContent>
                        {firmUsers.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            <div className="flex flex-col">
                              <span>{user.name}</span>
                              {user.name !== user.email && (
                                <span className="text-xs text-gray-500">{user.email}</span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={
              !privateUrl.trim() ||
              isLoading ||
              (credentialLevel === 'user' && !selectedUserId)
            }
          >
            {isLoading ? 'Adding...' : 'Add Item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
